import { Injectable } from '@angular/core';
import { saveAs } from 'file-saver';
import { Workbook, Worksheet } from 'exceljs';
import { Column } from '../models/column.model';

@Injectable({
	providedIn: 'root'
})
export class ExportTableService {
	readonly TYPE_EXCEL = 'xlsx';
	readonly EXCEL_EXTENSION = '.' + this.TYPE_EXCEL;
	readonly EXCEL_TYPE = 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet;charset=UTF-8';

	readonly TYPE_CSV = 'csv';
	readonly CSV_EXTENSION = '.' + this.TYPE_CSV;
	readonly CSV_TYPE = 'text/csv;charset=UTF-8';

	readonly defaultErrorCallback = (error) => console.log(error);

	/**
	 * Crée le workbook.
	 *
	 * @returns le workbook créé.
	 */
	createWorkBook(): Workbook {
		return new Workbook();
	}

	/**
	 * Ajoute une feuille à un workbook.
	 *
	 * @param workbook le workbook.
	 * @param sheetname le nom de la feuille.
	 * @param rows (optionnel) les données à insérer.
	 */
	addSheet(workbook: Workbook, sheetname: string, rows?: any[][]): Worksheet {
		const worksheet = workbook.addWorksheet(sheetname);

		if (rows) {
			worksheet.addRows(rows);
		}

		return worksheet;
	}

	/**
	 * Ajoute une feuille à un workbook.
	 * On définit les colonnes en indiquant la clé qui les représente et l'entête associée.
	 * Les données sont représentées sous la forme d'objets indiquant pour chaque clé de colonne la valeur associée.
	 *
	 * @param workbook le workbook.
	 * @param sheetname le nom de la feuille.
	 * @param columns les colonnes du tableau.
	 * @param rows les données à insérer.
	 */
	addSheetWithObjectRows(workbook: Workbook, sheetname: string, columns: Column[], rows: any[]): Worksheet {
		const worksheet = this.addSheet(workbook, sheetname);

		// Définition des colonnes
		worksheet.columns = columns.map(column => ({ header: column.header, key: column.key }));

		// Ajout des données
		worksheet.addRows(rows);

		return worksheet;
	}

	/**
	 * Enregistre le fichier au format Excel (xlsx)
	 *
	 * @param filename le nom du fichier
	 * @param workbook le fichier
	 * @param errorCallback callback exécuté en cas d'erreur.
	 */
	saveAsXlsx(filename: string, workbook: Workbook, errorCallback = this.defaultErrorCallback): void {
		workbook.xlsx.writeBuffer()
			.then((buffer: any) => saveAs(new Blob([buffer], { type: this.EXCEL_TYPE }), filename + this.EXCEL_EXTENSION))
			.catch(error => errorCallback(error));
	}

	/**
	 * Enregistre le fichier au format CSV.
	 *
	 * @param filename le nom du fichier.
	 * @param workbook le fichier.
	 * @param errorCallback callback exécuté en cas d'erreur.
	 */
	saveAsCsv(filename: string, workbook: Workbook, errorCallback = this.defaultErrorCallback): void {
		const options = {
			formatterOptions: {
				delimiter: ';',
				writeBOM: true
			}
		};

		workbook.csv.writeBuffer(options)
			.then((buffer: any) => saveAs(new Blob([buffer], { type: this.CSV_TYPE }), filename + this.CSV_EXTENSION))
			.catch(error => errorCallback(error));
	}
}
